// Copyright (c) Ping. All rights reserved.
//
// Tiny cross-surface "jump to a message" hand-off. The Activity feed (and
// any other surface that navigates to a specific message) records a pending
// target here, then routes to `/chat/<conversationId>`. The chat surface
// (`WebChatRoom`) consumes the pending target once its conversation +
// messages have loaded and either scrolls the timeline to the row OR — when
// the target is a folded THREAD reply — opens the thread panel on the reply's
// root. It stays a module singleton (not React state / a store lib) so the
// producer and the freshly-mounted chat consumer don't need a shared provider
// straddling the route switch.

/** A request to reveal `messageId` inside `conversationId`. */
export interface ChatJumpTarget {
  readonly conversationId: string;
  /** Undefined = just open the conversation (no specific row to reveal). */
  readonly messageId?: string | undefined;
}

let pending: ChatJumpTarget | null = null;
const listeners = new Set<() => void>();

/**
 * Record a pending jump. Overwrites any prior unconsumed target (the newest
 * click wins) and notifies subscribers so a chat surface already mounted on
 * the same conversation reacts immediately.
 */
export function requestChatJump(target: ChatJumpTarget): void {
  pending = target;
  for (const l of listeners) l();
}

/** Read the pending jump without consuming it. */
export function peekChatJump(): ChatJumpTarget | null {
  return pending;
}

/** Consume (clear) the pending jump once it has been handled. */
export function clearChatJump(): void {
  pending = null;
}

/** Subscribe to jump requests. Returns an unsubscribe. */
export function subscribeChatJump(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
